import moment from 'moment-timezone'

/**
 * Get week of month (1-5)
 * @param {string|moment.Moment} date - Date to calculate week for
 * @returns {number} Week number (1-5)
 */
export const getWeekOfMonth = (date) => {
  const momentDate = moment(date)
  const firstDayOfMonth = momentDate.clone().startOf('month')
  const dayOfMonth = momentDate.date()
  const firstDayOfWeek = firstDayOfMonth.day()
  const weekOfMonth = Math.ceil((dayOfMonth + firstDayOfWeek) / 7)
  return weekOfMonth
}

/**
 * Find the Nth occurrence of a day in a month
 * @param {number} year - Year
 * @param {number} month - Month (0-11)
 * @param {number} dayOfWeek - Day of week (0-6, 0 = Sunday)
 * @param {number} weekNumber - Week number (1-5)
 * @returns {moment.Moment} Moment object for the target date
 */
export const findNthWeekdayInMonth = (year, month, dayOfWeek, weekNumber) => {
  const firstDay = moment([year, month, 1])
  const firstDayOfWeek = firstDay.day()
  let targetDate

  // Calculate the date of the Nth occurrence
  const daysToAdd = (dayOfWeek - firstDayOfWeek + 7) % 7
  targetDate = firstDay.clone().add(daysToAdd, 'days')

  // Add weeks to get to the Nth week
  targetDate.add(weekNumber - 1, 'weeks')

  return targetDate
}

/**
 * Generate recurring dates based on pattern
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} pattern - Recurring pattern (weekly, monthly, etc.)
 * @param {number} totalSessions - Total number of sessions to generate
 * @param {Object} timeSlot - Time slot object with start and end
 * @param {Function} convertDateAndSlotToUTC - Function to convert date and slot to UTC
 * @param {number} sowSessionDuration - Session duration in minutes
 * @returns {Array} Array of date objects with date, slotStart, and slotEnd
 */
export const generateRecurringDates = (
  startDate,
  pattern,
  totalSessions,
  timeSlot,
  convertDateAndSlotToUTC,
  sowSessionDuration,
) => {
  const dates = []
  const start = moment(startDate)
  const dayOfWeek = start.day() // 0 = Sunday, 1 = Monday, etc.
  const weekOfMonth = getWeekOfMonth(startDate)
  let currentDate = start.clone()

  for (let i = 0; i < totalSessions; i++) {
    if (i === 0) {
      // First date is the selected date
      const firstDateStr = currentDate.format('YYYY-MM-DD')
      const { start: slotStart, end: slotEnd } = convertDateAndSlotToUTC(
        firstDateStr,
        timeSlot.start,
        timeSlot.end,
      )

      dates.push({
        date: firstDateStr,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd
          ? slotEnd.toISOString()
          : slotStart
              .clone()
              .add(sowSessionDuration || 0, 'minutes')
              .toISOString(),
      })
    } else {
      let nextDate

      if (pattern === 'weekly') {
        // Every week on the same day
        nextDate = currentDate.clone().add(1, 'week')
      } else if (pattern === 'alternate' || pattern === 'bi-weekly') {
        // Every alternate week (bi-weekly)
        nextDate = currentDate.clone().add(2, 'weeks')
      } else if (pattern === 'every-3-weeks') {
        // Every 3 weeks
        nextDate = currentDate.clone().add(3, 'weeks')
      } else if (pattern.startsWith('every-') && pattern.endsWith('-weeks')) {
        // Dynamic N weeks pattern (e.g., "every-4-weeks")
        const weeksMatch = pattern.match(/every-(\d+)-weeks/)
        if (weeksMatch) {
          const weeks = parseInt(weeksMatch[1], 10)
          nextDate = currentDate.clone().add(weeks, 'weeks')
        }
      } else if (pattern === 'monthly') {
        // Monthly on the same week and day
        nextDate = currentDate.clone().add(1, 'month')
        const nextMonth = nextDate.month()
        const nextYear = nextDate.year()
        nextDate = findNthWeekdayInMonth(
          nextYear,
          nextMonth,
          dayOfWeek,
          weekOfMonth,
        )
      } else if (pattern === 'bi-monthly') {
        // Every 2 months on the same week and day
        nextDate = currentDate.clone().add(2, 'months')
        const nextMonth = nextDate.month()
        const nextYear = nextDate.year()
        nextDate = findNthWeekdayInMonth(
          nextYear,
          nextMonth,
          dayOfWeek,
          weekOfMonth,
        )
      } else if (pattern === 'quarterly') {
        // Every 3 months on the same week and day
        nextDate = currentDate.clone().add(3, 'months')
        const nextMonth = nextDate.month()
        const nextYear = nextDate.year()
        nextDate = findNthWeekdayInMonth(
          nextYear,
          nextMonth,
          dayOfWeek,
          weekOfMonth,
        )
      } else if (pattern.startsWith('every-') && pattern.endsWith('-months')) {
        // Dynamic N months pattern (e.g., "every-6-months")
        const monthsMatch = pattern.match(/every-(\d+)-months/)
        if (monthsMatch) {
          const months = parseInt(monthsMatch[1], 10)
          nextDate = currentDate.clone().add(months, 'months')
          const nextMonth = nextDate.month()
          const nextYear = nextDate.year()
          nextDate = findNthWeekdayInMonth(
            nextYear,
            nextMonth,
            dayOfWeek,
            weekOfMonth,
          )
        }
      }

      if (nextDate && nextDate.isValid()) {
        // Convert next date + slot time to UTC (accounting for user's timezone)
        const nextDateStr = nextDate.format('YYYY-MM-DD')
        const { start: slotStart, end: slotEndFromConversion } =
          convertDateAndSlotToUTC(nextDateStr, timeSlot.start, timeSlot.end)

        const slotEnd = slotEndFromConversion
          ? slotEndFromConversion
          : slotStart.clone().add(sowSessionDuration || 0, 'minutes')

        dates.push({
          date: nextDate.format('YYYY-MM-DD'),
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
        })

        currentDate = nextDate
      }
    }
  }

  return dates
}
